import { BrowserInitConfig, BrowserReportEvent, createBrowserClient, BrowserCommandClient } from '@apmplus/web';
import { refreshPreStartContext } from '@apmplus/integrations/refreshPreStartContext';
import {
    CustomEventData,
    ElasticApm,
    ElasticApmOptions,
    Falsy,
    ApmOptions,
    NioApmPlusVueErrorHandlerPluginConfig,
    ReactInfo,
    ViewModel,
    ElasticEventPayload,
} from './model';
import { initAutoReportClickEventPlugin } from './plugins/auto_report_click_event';
import {
    getUserID,
    getEnv,
    getDeviceID,
    getRuntime,
    getNioAppVersion,
    CURRENT_SDK_VERSION,
    getMobileInfo,
    currentInLgContainer,
    ELASTIC_APM_VALUE,
    getApmType,
    syncLocalConfigFromRemote,
} from './util';
import { getOSInfo, dsBridgeRegister } from './jsb';
import { SPALoadPlugin } from '@apmplus/integrations';
import { init as initApm } from '@nio-wad/apm-rum/elastic/apm-rum/src/index';

type UnknownFunc = (...args: unknown[]) => void;

export default class NioApmPlusClient {
    clientName: string;
    options: ApmOptions;
    client: BrowserCommandClient | null = null;
    env: string | undefined;
    hasVueErrorHandlerPlugin: boolean = false;
    apm: ElasticApm | null = null;

    constructor(clientName: string, options: ApmOptions) {
        this.clientName = clientName;
        this.options = options;

        if ((!this.options.abTest && (options as ElasticApmOptions).serviceName) || 
            (this.options.abTest && getApmType() === ELASTIC_APM_VALUE)) {
            // 兼容plugins里的app
            if (!this.options.vueErrorHandler?.app && this.options?.plugins?.vueErrorHandler?.app) {
                this.options.vueErrorHandler = this.options?.plugins?.vueErrorHandler;
            }
            this.createApmInstance(this.options as ElasticApmOptions);
        } else {
            this.client = createBrowserClient();
        }

        if (this.options.abTest && getApmType() === undefined) {
            syncLocalConfigFromRemote(this.getEnv());
        }
    }

    public initialize() {
        const env = this.getEnv();
        this.env = env;

        if (this.client === null) return;

        const config: BrowserInitConfig = {
            aid: this.options.aid || 0,
            token: this.options.token || '',
            env: env,
            domain: this.options.domain || 'apmplus.nio.com',
            plugins: {
                jsError: this.options?.plugins?.jsError ?? {
                    captureGlobalAsync: true,
                },
                performance: this.options?.plugins?.performance ?? true,
                blankScreen: this.options?.plugins?.blankScreen ?? false,
                breadcrumb: this.options?.plugins?.breadcrumb ?? true,
                resource: this.options?.plugins?.resource ?? true,
                resourceError: this.options?.plugins?.resourceError ?? true,
                ajax: this.options?.plugins?.ajax ?? {
                    autoWrap: true,
                    collectBodyOnError: true,
                },
                fetch: this.options?.plugins?.fetch ?? {
                    autoWrap: true,
                    collectBodyOnError: true,
                },
                pageview: this.options?.plugins?.pageview ?? true,
            },
        };

        if (typeof this.options?.pid === 'string') {
            config.pid = this.options.pid;
        }

        if (typeof this.options?.release === 'string') {
            config.release = this.options.release;
        }

        if (!!this.options?.sample) {
            config.sample = this.options.sample;
        }

        if (Array.isArray(this.options?.integrations) && this.options.integrations.length > 0) {
            config.integrations = [refreshPreStartContext(), ...this.options.integrations];
        } else {
            config.integrations = [refreshPreStartContext()];
        }

        if (this.options?.plugins?.spaLoad) {
            config.integrations?.push?.(SPALoadPlugin());
        }

        const deviceID = this.getDeviceID();
        if (!!deviceID) {
            config.deviceId = deviceID;
        }

        const userID = this.getUserID();
        if (!!userID) {
            config.userId = userID;
        }

        this.addCommonContext();
        if (Array.isArray(this.options?.context)) {
            this.options.context.forEach((c) => {
                if (!!c?.key) {
                    this.addContext(c.key, c?.value ?? '');
                }
            });
        }

        this.client.init(config);

        this.initVueErrorHandlerPlugin(env);

        initAutoReportClickEventPlugin(this.client, 'standard', this.options?.plugins?.autoReportClickEvent);

        this.addBeforeSendHandler();

        this.client.start();
    }

    /**
     * 创建Elastic APM实例
     * @param options 
     * @returns 
     */
    public createApmInstance(options: ElasticApmOptions): ElasticApm {
        try {
            const apmOptions = {
                serviceName: options.serviceName,
                serviceVersion: options.release,
                environment: options.serviceEnv,
            };
            Object.assign(apmOptions, 
                options.userId != null && { userId: options.userId },
                options.deviceId != null && { deviceId: options.deviceId },
                options.context != null && { context: options.context },
                options.vueErrorHandler?.app != null && { app: options.vueErrorHandler.app },
                options.northPolarisSampleRateConfig != null && { northPolarisSampleRateConfig: options.northPolarisSampleRateConfig },
            );
            console.log('APM init', apmOptions);
            this.apm = initApm(apmOptions) as unknown as ElasticApm;
            console.log('APM initialized successfully');
            return this.apm;
        } catch (error) {
            console.error('APM initialization failed:', error);
            // 返回一个模拟的 APM 对象避免代码报错
            return {
                startTransaction: () => undefined,
                startSpan: () => undefined,
                captureError: async (): Promise<void> => {},
                warn: () => undefined,
                info: () => undefined,
                error: () => undefined,
                debug: () => undefined,
                addLabels: () => undefined,
                setCustomContext: () => undefined,
                setUserContext: () => undefined,
                destroy: () => undefined,
            };
        }
    }

    /**
     * 获取Elastic APM实例
     * @returns 
     */
    public getApmInstance(): ElasticApm | null {
        return this.apm;
    }

    private addBeforeSendHandler() {
        this.client?.on('beforeBuild', (ev: BrowserReportEvent): BrowserReportEvent | Falsy => {
            if (typeof this.options?.plugins?.qiankun?.getCurrentMicroAppReleaseVersion === 'function') {
                /**
                 * 待删除
                 * 上报日志前执行，返回当前页面所属 qiankun 主/子应用的 release 版本号，和子应用的 Sourcemap Release 版本号对应，用于解析 JS Error 的源代码
                 */
                try {
                    const correctRelease = this.options.plugins.qiankun.getCurrentMicroAppReleaseVersion({
                        url: ev?.extra?.url,
                    });
                    if (typeof correctRelease === 'string') {
                        if (typeof ev?.overrides !== 'object') {
                            ev.overrides = {
                                release: correctRelease,
                            };
                        } else {
                            ev.overrides.release = correctRelease;
                        }
                        if (typeof ev?.extra !== 'object') {
                            ev.extra = {
                                release: correctRelease,
                            };
                        } else {
                            ev.extra.release = correctRelease;
                        }
                    }
                } catch (err) {
                    console.error(err);
                }
            }

            if (ev.ev_type === 'js_error') {
                try {
                    /**
                     * 上报 JS Error 日志前执行，通过 getCurrentAppReleaseVersion 方法获取当前页面所属 qiankun 主/子应用的 release 版本号，用于解析 JS Error 的源代码
                     */
                    if (typeof this.options?.plugins?.qiankun?.getCurrentAppReleaseVersion === 'function') {
                        const correctRelease = this.options.plugins.qiankun.getCurrentAppReleaseVersion({
                            url: ev?.extra?.url,
                            name: ev?.payload?.error?.name,
                            message: ev?.payload?.error?.message,
                            stack: ev?.payload?.error?.stack,
                        });

                        if (typeof correctRelease === 'string') {
                            if (typeof ev?.overrides !== 'object') {
                                ev.overrides = {
                                    release: correctRelease,
                                };
                            } else {
                                ev.overrides.release = correctRelease;
                            }
                            if (typeof ev?.extra !== 'object') {
                                ev.extra = {
                                    release: correctRelease,
                                };
                            } else {
                                ev.extra.release = correctRelease;
                            }
                        }
                    }
                } catch (err) {
                    console.error(err);
                }
            }

            if (this.options?.isDebug) {
                /**
                 * DEBUG 模式，不上报日志数据至服务器，而通过 console.log 打印
                 */
                try {
                    console.log('[NIO APM Plus] SendEvent: ', ev);
                } catch (err) {
                    // do nothing
                }
                // 监听事件被发送之前的瞬间, 返回Falsy类型则不上报。
                return false;
            }

            return ev;
        });
    }

    private addCommonContext() {
        this.addContext('nio_apm_plus_version', CURRENT_SDK_VERSION);

        const runtime = getRuntime();
        this.addContext('nio_runtime', runtime);
        if (runtime === 'lg' || runtime === 'webf') {
            this.addContext('nio_app_version', getNioAppVersion());
            this.addMobileContext();
            this.registerNetworkTypeContext();
        }
    }

    private addMobileContext() {
        const mobileInfo = getMobileInfo();
        if (!!mobileInfo) {
            if (!!mobileInfo.brand) {
                this.addContext('nio_mobile_brand', mobileInfo.brand);
            }
            if (!!mobileInfo.model) {
                this.addContext('nio_mobile_model', mobileInfo.model);
            }
            if (!!mobileInfo.systemName) {
                this.addContext('nio_system_name', mobileInfo.systemName);
            }
            if (!!mobileInfo.systemVersion) {
                this.addContext('nio_system_version', mobileInfo.systemVersion);
            }
        }
    }

    /**
     * NIO App LG WebView 下
     * 安卓通过 getOSInfo 获取 network type ，后续通过网络状态变化更新 context
     * iOS 通过监听网络状态变化设置 context
     * @private
     */
    private registerNetworkTypeContext() {
        try {
            if (currentInLgContainer()) {
                const result = getOSInfo();
                const networkType = result?.data?.networkType;
                if (!!networkType) {
                    this.addContext('nio_network_type', networkType);
                }

                dsBridgeRegister({
                    event: 'ON_NET_WORK_CHANGE',
                    callback: (e: any) => {
                        try {
                            e = typeof e === 'string' ? JSON.parse(e) : e;
                            const { data } = e;
                            if (!!data && !!data.status) {
                                this.addContext('nio_network_type', data.status);
                            }
                        } catch (err) {
                            console.log(err);
                        }
                    },
                });
            }
        } catch (err) {
            console.log(err);
        }
    }

    private initVueErrorHandlerPlugin(env: string) {
        if (this.hasVueErrorHandlerPlugin) {
            return;
        }

        if (
            !this.options?.plugins?.vueErrorHandler ||
            !this.options?.plugins?.vueErrorHandler?.app ||
            !this.options?.plugins?.vueErrorHandler?.app?.config
        ) {
            return;
        }

        const app = this.options.plugins.vueErrorHandler.app;

        const { errorHandler, silent } = app.config;

        app.config.errorHandler = (error: Error, vm: ViewModel, lifecycleHook: string): void => {
            // Capture exception in the next event loop, to make sure that all breadcrumbs are recorded in time.
            setTimeout(() => {
                this.captureException(error);
            });

            if (typeof errorHandler === 'function') {
                (errorHandler as UnknownFunc).call(app, error, vm, lifecycleHook);
            }

            if (env !== 'prod') {
                try {
                    const hasConsole = typeof console !== 'undefined';
                    const message = `Error in ${lifecycleHook}: "${error && error.toString()}"`;
                    if (hasConsole && !silent) {
                        // eslint-disable-next-line no-console
                        console.error(`[Vue warn]: ${message}`);
                    }
                } catch (err) {
                    // do nothing
                }
            }
        };

        this.hasVueErrorHandlerPlugin = true;
    }

    private getDeviceID() {
        let deviceID = this.options.deviceId;
        if (deviceID === null || deviceID === void 0) {
            deviceID = getDeviceID();
        }
        return deviceID;
    }

    private getEnv() {
        let env = this.options.env || this.options.serviceEnv;
        if (env === null || env === void 0) {
            env = getEnv();
        }
        return env;
    }

    private getUserID() {
        let userID = this.options.userId;
        if (userID === null || userID === void 0) {
            userID = getUserID();
        }
        return userID;
    }

    /**
     * 添加 Vue Error 捕获插件
     * @param vueErrorHandlerOption
     */
    addVueErrorHandlerPlugin(vueErrorHandlerOption: NioApmPlusVueErrorHandlerPluginConfig) {
        if (!this.env) {
            return;
        }

        if (!this.options?.plugins) {
            this.options.plugins = {};
        }

        this.options.plugins.vueErrorHandler = vueErrorHandlerOption;
        this.initVueErrorHandlerPlugin(this.env);
    }

    /**
     * 获取 SDK 当前配置
     * 如果 SDK 还未 init，则会返回 undefined
     */
    getConfig() {
        return this.client?.config();
    }

    /**
     * 更改通用上下文，仅对更新后发送的事件生效。
     * 如果需要对所有事件生效，请确保在start之前调用。
     * @param config
     */
    updateConfig(config: Partial<BrowserInitConfig>) {
        return this.client?.config(config);
    }

    /**
     * 销毁实例
     * 调用此方法可以去掉所有监听和副作用，包括hook或者内存对象。
     * 当前实例本身还是可用的，所以手动调用API依然可以上报数据。
     */
    destroy() {
        this.client?.destroy();
        this.client = null;
        this.apm?.destroy();
        this.apm = null;
    }

    /**
     * 上报自定义日志
     * 注意格式，格式不符合的事件将会试图转换，无法转换的事件将会被忽略。
     * @param content
     * @param extra
     */
    debug(content: string, extra?: { [key: string]: string | number }) {
        try {
            if (this.client) {
                this.client?.sendLog?.({
                    level: 'debug',
                    content: content,
                    extra: extra,
                });
            } else if (this.apm) {
                this.apm?.debug(content, extra);
            }
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * 上报自定义日志
     * 注意格式，格式不符合的事件将会试图转换，无法转换的事件将会被忽略。
     * @param content
     * @param extra
     */
    info(content: string, extra?: { [key: string]: string | number }) {
        try {
            if (this.client) {
                this.client?.sendLog?.({
                    level: 'info',
                    content: content,
                    extra: extra,
                });
            } else if (this.apm) {
                this.apm?.info(content, extra);
            }
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * 上报自定义日志
     * 注意格式，格式不符合的事件将会试图转换，无法转换的事件将会被忽略。
     * @param content
     * @param extra
     */
    warn(content: string, extra?: { [key: string]: string | number }) {
        try {
            if (this.client) {
                this.client?.sendLog?.({
                    level: 'warn',
                    content: content,
                    extra: extra,
                });
            } else if (this.apm) {
                this.apm?.warn(content, extra);
            }
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * 上报自定义日志
     * 注意格式，格式不符合的事件将会试图转换，无法转换的事件将会被忽略。
     * @param content
     * @param extra
     */
    error(content: string, extra?: { [key: string]: string | number }) {
        try {
            if (this.client) {
                this.client?.sendLog?.({
                    level: 'error',
                    content: content,
                    extra: extra,
                });
            } else if (this.apm) {
                this.apm?.error(content, extra);
            }
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * 上报 JS 错误
     */
    captureException(error: any, extra?: Record<string, string>, react?: ReactInfo) {
        try {
            if (this.client) {
                this.client?.captureException?.(error, extra, react);
            } else if (this.apm) {
                this.apm?.captureError?.(error, extra as any);
            }
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * 上报自定义事件
     * 注意格式，格式不符合的事件将会试图转换，无法转换的事件将会被忽略。
     * 具体消费方式可以查看自定义监控(https://www.volcengine.com/docs/6431/104917)。
     */
    sendEvent(data: CustomEventData) {
        try {
            if (this.client) {
                this.client?.sendEvent?.(data);
            } else if (this.apm) {
                this.apm.sendEvent?.(data as ElasticEventPayload);
            }
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * 上报一次 PV
     * 若 pid 与当前 pid 相同则会忽略此次 PV
     * @param pid
     */
    sendPageView(pid: string) {
        try {
            this.client?.sendPageview?.(pid);
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * 设置 context 中的单个 key
     * @param key
     * @param value
     */
    addContext(key: string, value: any) {
        try {
            this.client?.('context.set', key, value);
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * 将 context 和传入的对象合并，生成新的 context
     * @param context
     */
    mergeContext(context: Record<string, any>) {
        try {
            this.client?.('context.merge', context);
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * 删除 context 中的某个 key
     * @param key
     */
    deleteContext(key: string) {
        try {
            this.client?.('context.delete', key);
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * 清空 context
     */
    clearContext() {
        try {
            this.client?.('context.clear');
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * 上报任意事件
     * 直接上报一个事件，具体格式见上报格式。
     * https://www.volcengine.com/docs/6431/104865
     * @param ev
     */
    report(ev: BrowserReportEvent) {
        try {
            this.client?.report(ev);
        } catch (err) {
            console.error(err);
        }
    }
}
